import React from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { User } from '../../types/user';
import { RoleSpace } from '../../utils/roleCatalog';
import { UserAvatar } from './UserAvatar';
import { suggestUsername, generatePassword, passwordStrength } from './passwordTools';
import { useCreateUser } from './createUser/useCreateUser';
import { AccessSection } from './createUser/AccessSection';
import { IdentitySection } from './createUser/IdentitySection';
import { PasswordSection } from './createUser/PasswordSection';

// Kept for the screens that imported the helpers from here.
export { suggestUsername, generatePassword, passwordStrength };

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUserCreated: (user: User) => void;
  /** Preselected client, when opened from inside a client's user list. */
  defaultClientId?: string;
  /** Its name, shown in the header and the organisation card. */
  defaultClientName?: string;
  /**
   * Which catalogue of roles to offer. 'client' creates a customer's
   * technician; 'anep' creates platform staff. Defaults to 'client' because
   * every entry point today is a client's user list.
   */
  space?: RoleSpace;
}

/**
 * The account creation form, in three blocks on one screen: the
 * organisation and the role, the identity, the password and status. The
 * footer sums up what is about to be created. The logic lives in
 * `useCreateUser`.
 */
export const CreateUserModal: React.FC<CreateUserModalProps> = ({ isOpen, onClose, onUserCreated, defaultClientId, defaultClientName, space = 'client' }) => {
  const { t } = useI18n();
  const form = useCreateUser({ isOpen, onClose, onUserCreated, defaultClientId, defaultClientName, space });
  const { isLoading, error, reactivateTarget, username, firstName, lastName, clientName, selectedRoleOption } = form;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={form.close}>
      <div className="modal-content create-user-modal" data-testid="modal-create-user" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <h2>{t('users.create.title')}</h2>
            {clientName && <span className="client-id-badge cu-client-badge" title={t('users.create.client')}>{clientName}</span>}
          </div>
          <button type="button" className="close-button" data-testid="btn-modal-close" onClick={form.close} disabled={isLoading} aria-label={t('common.close')}>×</button>
        </div>

        <form onSubmit={form.submit} noValidate>
          <div className="modal-body cu-body">
            {error && (
              <div className="error-banner" role="alert">
                <span className="error-icon">!</span>
                <span className="error-message">{error}</span>
                <button type="button" className="error-dismiss" onClick={() => form.setError(null)} aria-label={t('common.dismissError')}>×</button>
              </div>
            )}

            {reactivateTarget && (
              <div className="reactivate-offer" data-testid="reactivate-offer">
                <div className="reactivate-text">{t('users.create.reactivateOffer', { username: reactivateTarget.username })}</div>
                <button type="button" className="reactivate-button" data-testid="btn-reactivate-user" onClick={form.reactivate} disabled={isLoading}>
                  {isLoading ? t('common.loading') : t('users.create.reactivate')}
                </button>
              </div>
            )}

            <AccessSection form={form} />
            <IdentitySection form={form} />
            <PasswordSection form={form} />
          </div>

          <div className="modal-footer cu-footer">
            <div className="cu-summary" data-testid="cu-summary">
              {username.trim() ? (
                <>
                  <UserAvatar username={username.trim()} firstName={firstName} lastName={lastName} enabled={form.enabled} />
                  <span className="cu-summary-text">
                    <strong>{[firstName, lastName].filter(Boolean).join(' ') || username.trim()}</strong>
                    <span className="mono">{username.trim()}</span>
                    {selectedRoleOption && <span className="cu-summary-role">{selectedRoleOption.name}</span>}
                    {clientName && <span className="cu-summary-client">{clientName}</span>}
                  </span>
                </>
              ) : (
                <span className="cu-hint">{t('users.create.summaryEmpty')}</span>
              )}
            </div>
            <div className="cu-footer-actions">
              <button type="button" className="btn btn-secondary" data-testid="btn-cancel" onClick={form.close} disabled={isLoading}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" data-testid="btn-submit" disabled={!form.canSubmit}>
                {isLoading ? t('users.create.creating') : t('users.create.submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
